import React, { useEffect } from 'react';

const money = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const fechaHora = (value) => {
  if (!value) return '—';

  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

export default function RestauranteCuentaDetalleModal({
  abierto,
  cuenta,
  onClose,
  onAnularPago,
}) {
  useEffect(() => {
    if (!abierto) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [abierto, onClose]);

  if (!abierto || !cuenta) return null;

  const pedidos = cuenta.pedidos || [];
  const pagos = cuenta.pagos || [];
  const consumo = Number(cuenta.total || 0);
  const propina = Number(cuenta.propina_monto || 0);
  const totalFinal = consumo + propina;
  const totalPagado = pagos.reduce(
    (total, pago) => total + Number(pago.monto || 0),
    0
  );

  const mesa =
    cuenta.sesion?.mesa?.alias ||
    cuenta.sesion?.mesa?.nombre ||
    '—';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-azul-oscuro/80 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-blanco-puro/20 bg-azul-oscuro shadow-2xl">
        <div className="flex items-start justify-between border-b border-blanco-puro/10 px-6 py-5">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amarillo-dorado">
              Restaurante
            </div>

            <h2 className="mt-1 text-2xl font-bold text-blanco-puro">
              Cuenta #{cuenta.id}
            </h2>

            <div className="mt-1 text-sm text-blanco-puro/60">
              Cerrada {fechaHora(cuenta.cerrada_at)}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-blanco-puro/15 text-xl text-blanco-puro/70 transition hover:bg-blanco-puro/10 hover:text-blanco-puro"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="max-h-[calc(90vh-90px)] overflow-y-auto p-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
              <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                Mesa
              </div>
              <div className="mt-1 font-bold text-blanco-puro">
                {mesa}
              </div>
            </div>

            <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
              <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                Comensales
              </div>
              <div className="mt-1 font-bold text-blanco-puro">
                {cuenta.sesion?.comensales ?? '—'}
              </div>
            </div>

            <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
              <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                Abrió
              </div>
              <div className="mt-1 font-bold text-blanco-puro">
                {cuenta.sesion?.usuarioApertura?.nombre || '—'}
              </div>
            </div>

            <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
              <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                Cerró
              </div>
              <div className="mt-1 font-bold text-blanco-puro">
                {cuenta.sesion?.usuarioCierre?.nombre || '—'}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-blanco-puro/50">
              Consumo
            </div>

            <div className="overflow-hidden rounded-2xl border border-blanco-puro/10">
              {pedidos.length === 0 ? (
                <div className="p-4 text-sm text-blanco-puro/50">
                  Sin productos registrados.
                </div>
              ) : (
                <div className="divide-y divide-blanco-puro/10">
                  {pedidos.map((pedido) => (
                    <div
                      key={pedido.id}
                      className="bg-blanco-puro/[0.03] p-4"
                    >
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm font-bold text-amarillo-dorado">
                          Comanda #{pedido.id}
                        </div>

                        <div className="text-xs text-blanco-puro/50">
                          {pedido.usuario?.nombre || '—'} ·{' '}
                          {pedido.estado || '—'}
                        </div>
                      </div>

                      <div className="space-y-3">
                        {(pedido.items || []).map((item) => (
                          <div
                            key={item.id}
                            className="grid grid-cols-[1fr_auto] gap-4"
                          >
                            <div>
                              <div className="font-semibold text-blanco-puro">
                                {item.producto?.nombre || 'Producto'}
                              </div>

                              <div className="mt-0.5 text-xs text-blanco-puro/50">
                                {Number(item.cantidad || 0)} ×{' '}
                                {money(item.precio_unitario)}
                                {item.producto?.codigo
                                  ? ` · ${item.producto.codigo}`
                                  : ''}
                              </div>
                            </div>

                            <div className="self-center font-bold text-blanco-puro">
                              {money(item.subtotal)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6">
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-blanco-puro/50">
              Pagos
            </div>

            <div className="overflow-hidden rounded-2xl border border-blanco-puro/10">
              {pagos.length === 0 ? (
                <div className="p-4 text-sm text-blanco-puro/50">
                  Sin pagos registrados.
                </div>
              ) : (
                <div className="divide-y divide-blanco-puro/10">
                  {pagos.map((pago) => (
                    <div
                      key={pago.id}
                      className="grid gap-2 bg-blanco-puro/[0.03] p-4 sm:grid-cols-[1fr_1fr_auto] sm:items-center"
                    >
                      <div>
                        <div className="font-bold text-blanco-puro">
                          {pago.medioPago?.nombre || 'Medio de pago'}
                        </div>

                        <div className="mt-0.5 text-xs text-blanco-puro/50">
                          {fechaHora(pago.pagado_at)}
                        </div>
                      </div>

                      <div className="text-sm text-blanco-puro/70">
                        Cobró: {pago.usuario?.nombre || '—'}
                      </div>

                      <div className="flex items-center justify-end gap-3 text-right text-base font-bold text-blanco-puro">
                        {money(pago.monto)}

                        {onAnularPago && (
                          <button
                            type="button"
                            onClick={() => onAnularPago(pago)}
                            aria-label={`Anular el pago de ${money(
                              pago.monto
                            )}`}
                            className="text-xs font-bold text-red-300 underline-offset-2 transition hover:underline"
                          >
                            Anular
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
              <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                Consumo
              </div>
              <div className="mt-1 text-xl font-black text-blanco-puro">
                {money(consumo)}
              </div>
            </div>

            <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
              <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                Propina
              </div>
              <div className="mt-1 text-xl font-black text-blanco-puro">
                {money(propina)}
              </div>
            </div>

            <div className="rounded-2xl border border-blanco-puro/10 bg-blanco-puro/5 p-4">
              <div className="text-xs uppercase tracking-wide text-blanco-puro/50">
                Pagado
              </div>
              <div className="mt-1 text-xl font-black text-blanco-puro">
                {money(totalPagado)}
              </div>
            </div>

            <div className="rounded-2xl border border-amarillo-dorado/30 bg-amarillo-dorado/10 p-4">
              <div className="text-xs font-bold uppercase tracking-wide text-amarillo-dorado">
                Total
              </div>
              <div className="mt-1 text-xl font-black text-amarillo-dorado">
                {money(totalFinal)}
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-blanco-puro/20 px-5 py-2.5 text-sm font-bold text-blanco-puro transition hover:bg-blanco-puro/10"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
